import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Grid, Stack } from '@mui/material';
import useCoupons from 'api/useCoupons';
import useOffers from 'api/useOffers';
import {
  Coupon,
  EPublisherSolutionModel,
  Offer,
  PromoCode
} from 'common/contracts';
import { EButtonColor, ELocalStorageKeys, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { localStorageUtil } from 'utils/localStorageUtil';
import * as yup from 'yup';

import AcContentWrapper from '../../../components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from '../../../components/AcViewWrapper/AcViewWrapper';
import PageTopBar from '../../../components/Topbar/PageTopBar';
import { EToasterStatus, showToast } from '../../../utils/showToast';

import '../../../style/forms.scss';
import CouponsGeneralSettings from './components/CouponsGeneralSettings';
import CouponsOffersSettings from './components/CouponsOffersSettings';
import CouponsPromoCodes from './components/CouponsPromoCodes';
import CouponsPlayerSettings from './components/CouponsPlayersSettings/CouponsPlayerSettings';
import DialogModal from '../../../components/Dialog/Dialog';

interface ICouponsFormProps {
  edit?: boolean;
  dup?: boolean;
}

export interface CouponFormValues {
  name: string;
  active: true;
  discountPercentage: string | number;
  maxRedemptionsPerCustomer: string | number;
  expiredBy: string;
  firstTimePurchase: boolean;
  supportedOffersBySku: any[];
  promoCodes: PromoCode[];
  allowedPlayers: string[];
}

export const CouponsForm: React.FC<ICouponsFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { couponId } = useParams();

  const { getSingleCoupon, addCoupon, updateCoupon, validatePromoCodeName } =
    useCoupons(publisherId, couponId);
  const { getCoupons } = useCoupons(publisherId);
  const [neverExpires, setNeverExpires] = useState(false);
  const [isUnSavedChangesModalOpen, setIsUnSavedChangesModalOpen] =
    useState(false);

  const [promoCodes, setPromoCodes] = useState<
    {
      _id: string;
      name: string;
      maxRedemptions: number;
      creationDate: string;
      active: boolean;
    }[]
  >([]);
  const [promoCode, setPromoCode] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState<string | number>('');

  useEffect(() => {
    setPromoCodes(
      (getSingleCoupon.data?.promoCodes as any)?.map((promoCode: any) => ({
        _id: promoCode.id || '',
        name: promoCode.name || '',
        maxRedemptions: promoCode.maxRedemptions || 0,
        active: promoCode.active || false
      }))
    );
  }, [getSingleCoupon.data]);

  const { getOffers } = useOffers({
    offerType: OfferType.BUNDLE,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const { getOffers: getSpecialOffers } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const { getOffers: getPopupOffers } = useOffers({
    offerType: OfferType.POPUP,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const checkUniqueness = (newName: string = '') => {
    const couponsNames = getCoupons.data?.map((coupons) => coupons.name);
    if (edit) {
      const currentCouponName = getSingleCoupon.data?.name || '';
      return !couponsNames
        ?.filter((CouponName) => CouponName !== currentCouponName)
        .includes(newName);
    }
    return !couponsNames?.includes(newName);
  };

  let couponSchema = yup.object().shape({
    name: yup
      .string()
      .required('Coupon name is required')
      .test('uniqueness', 'Coupon name already exists', (value) => {
        return checkUniqueness(value);
      }),
    active: yup.boolean().required('Coupon status is required'),
    discountPercentage: yup
      .number()
      .integer('Discount percentage must be a whole number')
      .min(1, 'Discount percentage must be at least 1')
      .max(99, 'Discount percentage must be at most 99')
      .required('Discount percentage is required'),
    maxRedemptionsPerCustomer: yup
      .number()
      .min(0, 'Max redemptions per customer must be greater than or equal to 1')
      .notRequired(),
    expiredBy: yup
      .date()
      .nullable()
      .min(dayjs(), 'Expires on date should be in the future')
      .when('neverExpires', {
        is: false,
        then: yup
          .date()
          .required('Expiry date is required when not set to never expire')
      }),
    firstTimePurchase: yup.boolean(),
    ...(isCheckoutUser
      ? {}
      : {
          supportedOffersBySku: yup.array().of(yup.string())
        }),
    promoCodes: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Promo code name is required'),
          maxRedemptions: yup
            .number()
            .min(0, 'Max redemptions must be greater than or equal to 0')
            .required('Max redemptions is required')
        })
      )
      .min(1, 'At least one promo code must be added')
  });

  const getOffersForSelect = (
    supportedOffersByType: string[] = ['bundles', 'promotions', 'popups']
  ) => {
    const offers = [];
    if (
      getOffers?.data?.offers &&
      Array.isArray(getOffers.data.offers) &&
      supportedOffersByType.includes('bundles')
    ) {
      offers.push(
        ...getOffers.data.offers.map((offer: Offer) => ({
          content: offer.name,
          key: offer.publisherOfferId,
          value: offer.name
        }))
      );
    }
    if (
      getSpecialOffers?.data?.offers &&
      Array.isArray(getSpecialOffers.data.offers) &&
      supportedOffersByType.includes('promotions')
    ) {
      offers.push(
        ...getSpecialOffers.data.offers.map((offer: Offer) => ({
          content: offer.name,
          key: offer.publisherOfferId,
          value: offer.name
        }))
      );
    }
    if (
      getPopupOffers?.data?.offers &&
      Array.isArray(getPopupOffers.data.offers) &&
      supportedOffersByType.includes('popups')
    ) {
      offers.push(
        ...getPopupOffers.data.offers.map((offer: Offer) => ({
          content: offer.name,
          key: offer.publisherOfferId,
          value: offer.name
        }))
      );
    }
    return offers;
  };

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    errors,
    dirty,
    isSubmitting,
    touched,
    isValid,
    setFieldValue,
    setFieldTouched
  } = useFormik({
    validateOnMount: true,
    validationSchema: couponSchema,
    enableReinitialize: true,
    initialValues: {
      name: getSingleCoupon.data?.name || '',
      active: getSingleCoupon.data?.active || true,
      discountPercentage: getSingleCoupon.data?.discountPercentage || '',
      maxRedemptionsPerCustomer:
        getSingleCoupon.data?.maxRedemptionsPerCustomer || '',
      expiredBy: getSingleCoupon.data?.expiredBy || '',
      firstTimePurchase: getSingleCoupon.data?.firstTimePurchase || false,
      supportedOffersBySku: isCheckoutUser
        ? []
        : getSingleCoupon.data?.supportedOffersBySku ||
          getOffersForSelect()?.map((offer) => offer.key),
      promoCodes: getSingleCoupon.data?.promoCodes || [],
      allowedPlayers: getSingleCoupon.data?.allowedPlayers || []
    },
    onSubmit: async (values, helpers) => {
      let promoCodes = [];
      promoCodes = values.promoCodes.map((promoCode) => {
        const promoCodeData = {
          name: promoCode.name,
          maxRedemptions: promoCode.maxRedemptions,
          active: promoCode.active,
          fresh: promoCode.fresh,
          ...(edit ? { id: (promoCode as PromoCode)._id } : {})
        } as PromoCode;
        if (promoCodeData.id === '' || !!promoCodeData.fresh) {
          delete promoCodeData.id;
        }
        return promoCodeData;
      });

      const couponData = {
        ...values,
        promoCodes,
        expiredBy: neverExpires ? null : values.expiredBy,
        discountPercentage: Number(values.discountPercentage),
        maxRedemptionsPerCustomer: values.maxRedemptionsPerCustomer
          ? Number(values.maxRedemptionsPerCustomer)
          : null
      };

      if (edit) {
        updateCoupon.mutate(
          {
            ...couponData,
            promoCodes: couponData.promoCodes.map((code) => ({
              ...code
            }))
          },
          {
            onSuccess: () => {
              const newPromoCodes = values.promoCodes.map((promoCode) => ({
                ...promoCode,
                fresh: false
              }));
              helpers.setFieldValue('promoCodes', newPromoCodes);
              showToast({
                message: `Coupon updated successfully`,
                status: EToasterStatus.SUCCESS
              });
            },
            onError: (error: any) => {
              const serverError = error?.response?.data?.message;
              showToast({
                message: serverError || `Error updating coupon`,
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        addCoupon.mutate(
          {
            ...couponData,
            promoCodes: couponData.promoCodes.map((code) => ({
              ...code,
              active: true
            }))
          },
          {
            onSuccess: () => {
              const newPromoCodes = values.promoCodes.map((promoCode) => ({
                ...promoCode,
                fresh: false
              }));
              helpers.setFieldValue('promoCodes', newPromoCodes);
              showToast({
                message: `Coupon created successfully`,
                status: EToasterStatus.SUCCESS
              });
            },
            onError: (error: any) => {
              const serverError = error?.response?.data?.message;
              showToast({
                message: serverError || `Error creating coupon`,
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      }
    }
  });

  const isSaveButtonDisabled = edit
    ? !isValid || !dirty || isSubmitting
    : !isValid || addCoupon.isSuccess || addCoupon.isLoading;

  return (
    <>
      <AcViewWrapper
        header={
          <PageTopBar
            disable={false}
            headline={`${edit ? 'Edit' : 'New'} Coupon ${
              edit
                ? `(${
                    !getSingleCoupon.isLoading && getSingleCoupon.data
                      ? getSingleCoupon.data.name
                      : '...'
                  })`
                : ''
            }`}
            buttons={[
              {
                text: 'Save',
                action: () => submitForm(),
                disabled: isSaveButtonDisabled
              }
            ]}
            backFunction={() => {
              if (dirty) {
                setIsUnSavedChangesModalOpen(true);
              } else {
                navigate(-1);
              }
            }}
          />
        }
      >
        <AcContentWrapper className="formContent">
          <Grid container pt={'24px'}>
            <Grid item xs={12}>
              <Stack>
                <CouponsGeneralSettings
                  couponFormValues={values}
                  edit={edit}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  neverExpires={neverExpires}
                  dup={dup}
                  updatedAt={getSingleCoupon.data?.updatedAt}
                />

                {!isCheckoutUser && (
                  <CouponsOffersSettings
                    supportedOffersBySku={values.supportedOffersBySku}
                    getOffersForSelect={getOffersForSelect}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors as FormikErrors<Coupon>}
                    touched={touched as FormikTouched<Coupon>}
                    setFieldValue={setFieldValue}
                  />
                )}

                <CouponsPlayerSettings
                  couponFormValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldValue={setFieldValue}
                  dup={dup}
                />

                <CouponsPromoCodes
                  couponFormValues={values}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  maxRedemptions={maxRedemptions}
                  setMaxRedemptions={setMaxRedemptions}
                  validatePromoCodeName={validatePromoCodeName}
                />
              </Stack>
            </Grid>
          </Grid>
        </AcContentWrapper>
      </AcViewWrapper>

      <DialogModal
        isOpen={isUnSavedChangesModalOpen}
        headline="Unsaved Changes"
        text="You changed a promo code but didn’t save the coupon. Leaving will lose your changes."
        buttons={[
          {
            text: 'Proceed',
            color: EButtonColor.ERROR,
            variant: 'outlined',
            func: () => {
              navigate(-1);
            }
          },
          {
            text: 'Save Coupon',
            color: EButtonColor.SECONDARY,
            variant: 'contained',
            func: () => {
              submitForm();
              setIsUnSavedChangesModalOpen(false);
            }
          }
        ]}
        closeDialog={() => setIsUnSavedChangesModalOpen(false)}
      />
    </>
  );
};
